/**
 * CoffeePassPromo - Site-wide promotional components for Bula Coffee Pass
 * Includes: Sticky Banner, Homepage Hero, Menu Callout, Exit Intent Popup
 */
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, X, Sparkles, Gift, ArrowRight, Clock, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Custom event name for chat state
const CHAT_STATE_EVENT = 'bulaccino-chat-state';

// Routes where the Coffee Pass banner should NOT appear (checkout flow)
const HIDDEN_BANNER_ROUTES = [
  '/pay',           // Payment page
  '/checkout',      // Checkout page
  '/cart',          // Cart review
  '/order-review',  // Order review
  '/pos',           // POS terminal
  '/kiosk',         // Kiosk
  '/admin',         // Admin
  '/server',        // Server display
  '/pickup',        // Pickup display
  '/display',       // Display screens
];

// ============== STICKY BANNER (Site-wide, except checkout) ==============
export function CoffeePassStickyBanner({ onDismiss }) {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Check if current route should hide the banner
  const shouldHideBanner = HIDDEN_BANNER_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    // Check if user has dismissed the banner in this session
    const wasDismissed = sessionStorage.getItem('coffeePassBannerDismissed');
    if (wasDismissed) {
      setDismissed(true);
      setIsVisible(false);
    }
  }, []);

  // Listen for chat widget open/close events
  useEffect(() => {
    const handleChatState = (event) => {
      setChatOpen(event.detail?.isOpen || false);
    };
    
    window.addEventListener(CHAT_STATE_EVENT, handleChatState);
    return () => window.removeEventListener(CHAT_STATE_EVENT, handleChatState);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissed(true);
    sessionStorage.setItem('coffeePassBannerDismissed', 'true');
    if (onDismiss) onDismiss();
  };

  // Hide banner when chat is open, dismissed, or on checkout pages
  if (dismissed || !isVisible || chatOpen || shouldHideBanner) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary via-primary/95 to-amber-600 text-white shadow-lg"
      data-testid="coffee-pass-sticky-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm md:text-base font-medium">
            <span className="font-bold">Bula Coffee Pass</span>
            <span className="hidden sm:inline"> — </span>
            <br className="sm:hidden" />
            <span>$95<span className="text-xs opacity-80">/year</span> </span>
            <Link to="/bula-coffee-club" className="underline hover:no-underline">learn more</Link>
          </span>
        </div>
        <Link to="/bula-coffee-club">
          <Button 
            size="sm" 
            variant="secondary"
            className="bg-white text-primary hover:bg-white/90 rounded-full text-xs md:text-sm font-semibold px-4"
          >
            Join Now
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
        <button
          onClick={handleDismiss}
          className="absolute right-2 md:right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============== HOMEPAGE HERO SECTION ==============
export function CoffeePassHomeHero() {
  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-amber-50 via-white to-primary/5 dark:from-amber-900/20 dark:via-background dark:to-primary/10 border-y border-amber-200/50 dark:border-amber-800/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              EXCLUSIVE OFFER
            </div>
            
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
              Bula Coffee Pass
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              Get <strong className="text-foreground">1 FREE coffee every single day</strong> for a whole year. 
              Hot coffee only. Just $95<span className="text-sm">/year</span>.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
                <Coffee className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-sm">365 Free Coffees</p>
                  <p className="text-xs text-muted-foreground">1,825 value</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
                <Clock className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-sm">7-10 AM or 1-3:30 PM</p>
                  <p className="text-xs text-muted-foreground">Two daily windows</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
                <Gift className="w-5 h-5 text-pink-500" />
                <div>
                  <p className="font-medium text-sm">Gift-able</p>
                  <p className="text-xs text-muted-foreground">Share the love</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                <Award className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-sm text-amber-700 dark:text-amber-400">365-Day Bonus</p>
                  <p className="text-xs text-amber-600 dark:text-amber-500">100 gift card</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/bula-coffee-club">
                <Button size="lg" className="rounded-full px-8 w-full sm:w-auto">
                  <Coffee className="w-5 h-5 mr-2" />
                  Join for $95<span className="text-sm ml-1 opacity-80">/year</span>
                </Button>
              </Link>
              <Link to="/bula-coffee-club#how-it-works">
                <Button size="lg" variant="outline" className="rounded-full px-8 w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-primary to-amber-600 rounded-3xl p-8 text-white text-center">
              <div className="absolute -top-4 -right-4 bg-accent text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                BEST VALUE
              </div>
              
              <Coffee className="w-16 h-16 mx-auto mb-4 opacity-90" />
              
              <div className="mb-4">
                <span className="text-5xl md:text-6xl font-bold">60</span>
                <span className="text-xl opacity-90">/year</span>
              </div>
              
              <p className="text-lg opacity-90 mb-4">
                That&apos;s just <strong>16 cents per coffee!</strong>
              </p>
              
              <ul className="text-left space-y-2 mb-6">
                {[
                  '1 FREE 8oz coffee daily',
                  'Hot coffee only - your choice',
                  'Hot or iced',
                  '+3 upgrade to 12oz',
                  '365-day streak = 100 bonus'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/bula-coffee-club" className="block">
                <Button size="lg" className="w-full bg-white text-primary hover:bg-white/90 rounded-full font-semibold">
                  Get Your Pass Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============== MENU PAGE CALLOUT ==============
export function CoffeePassMenuCallout({ compact = false }) {
  if (compact) {
    return (
      <Link 
        to="/bula-coffee-club"
        className="block p-3 bg-gradient-to-r from-primary/10 to-amber-500/10 rounded-xl border border-primary/20 hover:border-primary/40 transition-colors"
        data-testid="coffee-pass-menu-callout-compact"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/20 rounded-lg">
            <Coffee className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Get this FREE daily!</p>
            <p className="text-xs text-muted-foreground">Bula Coffee Pass - $95/year</p>
          </div>
          <ArrowRight className="w-4 h-4 text-primary" />
        </div>
      </Link>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-gradient-to-r from-amber-50 to-primary/5 dark:from-amber-900/20 dark:to-primary/10 rounded-2xl p-6 border border-amber-200 dark:border-amber-800"
      data-testid="coffee-pass-menu-callout"
    >
      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="p-4 bg-primary/10 rounded-2xl">
          <Coffee className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-heading text-xl mb-1">Enjoy This Coffee FREE Every Day!</h3>
          <p className="text-muted-foreground text-sm">
            With Bula Coffee Pass, get 1 free 8oz coffee daily for just $95/year. 
            That&apos;s 365 coffees for the price of 6!
          </p>
        </div>
        <Link to="/bula-coffee-club">
          <Button className="rounded-full whitespace-nowrap">
            <Sparkles className="w-4 h-4 mr-2" />
            Learn More
          </Button>
        </Link>
      </div>
    </motion.div>
  );
}

// ============== EXIT INTENT POPUP ==============
export function CoffeePassExitPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const location = useLocation();

  // Don't show on bula-coffee-club page (they're already looking at Coffee Pass)
  // or on order/checkout pages
  const isExcludedPage = location.pathname.includes('/bula-coffee-club') || 
                         location.pathname.includes('/order') ||
                         location.pathname.includes('/checkout') ||
                         location.pathname.includes('/pay');

  useEffect(() => {
    // Don't show on excluded pages
    if (isExcludedPage) return;
    
    // Check if already shown this session
    const wasShown = sessionStorage.getItem('coffeePassExitShown');
    if (wasShown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Trigger when mouse leaves top of viewport (exit intent)
      if (e.clientY <= 0 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('coffeePassExitShown', 'true');
      }
    };

    // Only add listener after a delay (don't show immediately)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown, isExcludedPage]);

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={handleClose}
          data-testid="coffee-pass-exit-popup"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border border-border rounded-3xl p-8 max-w-md w-full shadow-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Coffee className="w-10 h-10 text-primary" />
              </div>
              
              <h3 className="font-heading text-2xl mb-2">Wait! Don&apos;t Miss This!</h3>
              <p className="text-muted-foreground mb-6">
                Get <strong className="text-foreground">365 FREE coffees</strong> for just $95/year with our Bula Coffee Pass!
              </p>

              <div className="bg-gradient-to-r from-primary/10 to-amber-500/10 rounded-2xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-3xl font-bold text-primary">$95</span>
                  <span className="text-sm text-muted-foreground">/year</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  1 free hot coffee every day • 7-10 AM or 1-3:30 PM
                </p>
              </div>

              <div className="space-y-3">
                <Link to="/bula-coffee-club" className="block">
                  <Button size="lg" className="w-full rounded-full">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Yes! Show Me The Deal
                  </Button>
                </Link>
                <button
                  onClick={handleClose}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  No thanks, I prefer paying full price
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ============== ORDER CONFIRMATION UPSELL ==============
export function CoffeePassOrderUpsell({ onClose }) {
  return (
    <div className="bg-gradient-to-r from-amber-50 to-primary/5 dark:from-amber-900/20 dark:to-primary/10 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-primary/20 rounded-xl">
          <Coffee className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-heading text-lg">Love our coffee?</h4>
            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-medium rounded-full">
              SAVE $$$
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Get your daily coffee FREE with Bula Coffee Pass! Just $95/year for 365 coffees.
          </p>
          <div className="flex gap-2">
            <Link to="/bula-coffee-club">
              <Button size="sm" className="rounded-full">
                Learn More
              </Button>
            </Link>
            {onClose && (
              <Button size="sm" variant="ghost" onClick={onClose}>
                Maybe Later
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== NAVBAR LINK WITH BADGE ==============
export function CoffeePassNavLink({ className = "", mobile = false }) {
  if (mobile) {
    return (
      <Link
        to="/bula-coffee-club"
        className={`flex items-center gap-2 py-2 text-lg font-medium text-primary ${className}`}
        data-testid="mobile-nav-coffee-pass"
      >
        <Coffee className="w-5 h-5" />
        Coffee Pass
        <span className="px-2 py-0.5 bg-accent text-white text-xs font-bold rounded-full">
          NEW
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/bula-coffee-club"
      className={`flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors ${className}`}
      data-testid="nav-coffee-pass"
    >
      <Coffee className="w-4 h-4" />
      Coffee Pass
      <span className="px-1.5 py-0.5 bg-accent text-white text-[10px] font-bold rounded-full leading-none">
        NEW
      </span>
    </Link>
  );
}
